// UI Slice
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface ModalState {
  signIn: boolean;
  trade: boolean;
  deposit: boolean;
  referral: boolean;
}

interface PositionActionModalState {
  isOpen: boolean;
  action: 'split' | 'merge';
  conditionId: string;
  tokenIds: string[];
  eventId?: string;
}

interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

interface UIState {
  modals: ModalState;
  positionActionModal: PositionActionModalState;
  sidebarOpen: boolean;
  mobileMenuOpen: boolean;
  toasts: ToastMessage[];
  isLoading: boolean;
}

const initialState: UIState = {
  modals: {
    signIn: false,
    trade: false,
    deposit: false,
    referral: false,
  },
  positionActionModal: {
    isOpen: false,
    action: 'split',
    conditionId: '',
    tokenIds: [],
    eventId: undefined,
  },
  sidebarOpen: true,
  mobileMenuOpen: false,
  toasts: [],
  isLoading: false,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    openModal: (state, action: PayloadAction<keyof ModalState>) => {
      state.modals[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof ModalState>) => {
      state.modals[action.payload] = false;
    },
    toggleModal: (state, action: PayloadAction<keyof ModalState>) => {
      state.modals[action.payload] = !state.modals[action.payload];
    },
    openPositionActionModal: (
      state,
      action: PayloadAction<{
        action: 'split' | 'merge';
        conditionId: string;
        tokenIds: string[];
        eventId?: string;
      }>
    ) => {
      state.positionActionModal = {
        isOpen: true,
        action: action.payload.action,
        conditionId: action.payload.conditionId,
        tokenIds: action.payload.tokenIds,
        eventId: action.payload.eventId,
      };
    },
    closePositionActionModal: (state) => {
      state.positionActionModal.isOpen = false;
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    toggleMobileMenu: (state) => {
      state.mobileMenuOpen = !state.mobileMenuOpen;
    },
    setMobileMenuOpen: (state, action: PayloadAction<boolean>) => {
      state.mobileMenuOpen = action.payload;
    },
    addToast: (state, action: PayloadAction<Omit<ToastMessage, 'id'>>) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      state.toasts.push({ ...action.payload, id });
    },
    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter((toast) => toast.id !== action.payload);
    },
    clearToasts: (state) => {
      state.toasts = [];
    },
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
});

export const {
  openModal,
  closeModal,
  toggleModal,
  openPositionActionModal,
  closePositionActionModal,
  toggleSidebar,
  setSidebarOpen,
  toggleMobileMenu,
  setMobileMenuOpen,
  addToast,
  removeToast,
  clearToasts,
  setGlobalLoading,
} = uiSlice.actions;

export default uiSlice.reducer;
